"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import dynamic from "next/dynamic";
import { useVisibilityRef } from "@/hooks/useVisibilityRef";
import Link from "next/link";
import {
  Radio,
  Globe2,
  TrendingUp,
  Crosshair,
  Shield,
  Layers,
  PanelLeftOpen,
  PanelLeftClose,
  Map as MapIcon,
} from "lucide-react";

const GeopoliticsFullMap = dynamic(
  () => import("./components/GeopoliticsFullMap"),
  { ssr: false }
);

// ─── Types ────────────────────────────────────────────────────────────
interface Article {
  title: string;
  url: string;
}

interface EnhancedConflictEvent {
  id: string;
  name: string;
  coordinates: [number, number]; // [lon, lat] from GDELT
  mentions: number;
  articles: Article[];
  type: "conflict" | "diplomatic";
  intensity: "low" | "medium" | "high" | "critical";
}

interface BilateralTension {
  id: string;
  country1: { name: string; code: string; coordinates: [number, number] };
  country2: { name: string; code: string; coordinates: [number, number] };
  mentions: number;
  articles: Article[];
  type: "conflict" | "sanctions" | "nuclear" | "diplomacy";
}

interface EnhancedStats {
  totalConflictZones: number;
  totalDiplomaticEvents: number;
  totalMentions: number;
  topRegion: string;
  criticalZones: number;
}

interface GeoMarket {
  id: string;
  slug: string;
  title: string;
  image: string | null;
  volume: number;
  volume24h: number;
  liquidity: number;
  endDate: string | null;
  active: boolean;
  closed: boolean;
  markets: Array<{
    id: string;
    question: string;
    slug: string;
    outcomes: string[];
    outcomePrices: number[];
    volume: number;
  }>;
  tags: Array<{ slug: string; label: string }>;
}

export interface GeolocatedMarket extends GeoMarket {
  lat: number;
  lon: number;
}

// ─── Multi-select filter state ────────────────────────────────────────
interface MapFilters {
  layers: {
    markets: boolean;
    conflicts: boolean;
    diplomatic: boolean;
    zones: boolean;
  };
  volumeMin: number;
  marketCategory: string | null;
  marketStatus: "all" | "new" | "closing-soon";
}

const DEFAULT_FILTERS: MapFilters = {
  layers: {
    markets: true,
    conflicts: true,
    diplomatic: true,
    zones: true,
  },
  volumeMin: 0,
  marketCategory: null,
  marketStatus: "all",
};

const VOLUME_OPTIONS = [
  { value: 0, label: "All" },
  { value: 1_000, label: "$1K+" },
  { value: 10_000, label: "$10K+" },
  { value: 50_000, label: "$50K+" },
  { value: 100_000, label: "$100K+" },
];

const STATUS_OPTIONS: { value: MapFilters["marketStatus"]; label: string }[] = [
  { value: "all", label: "All" },
  { value: "new", label: "New 48h" },
  { value: "closing-soon", label: "Closing" },
];

// ─── Geolocation mapping ─────────────────────────────────────────────
const LOCATION_KEYWORDS: Record<string, [number, number]> = {
  russia: [55.75, 37.62],
  ukraine: [50.45, 30.52],
  kyiv: [50.45, 30.52],
  china: [39.9, 116.4],
  taiwan: [25.03, 121.56],
  iran: [35.69, 51.39],
  israel: [31.77, 35.21],
  gaza: [31.5, 34.47],
  palestine: [31.9, 35.2],
  "north korea": [39.02, 125.75],
  "south korea": [37.57, 126.98],
  india: [28.61, 77.21],
  pakistan: [33.69, 73.05],
  trump: [38.9, -77.04],
  biden: [38.9, -77.04],
  "united states": [38.9, -77.04],
  syria: [33.51, 36.29],
  iraq: [33.31, 44.37],
  afghanistan: [34.53, 69.17],
  yemen: [15.35, 44.21],
  saudi: [24.71, 46.68],
  turkey: [39.93, 32.86],
  nato: [50.85, 4.35],
  europe: [48.86, 2.35],
  mexico: [19.43, -99.13],
  venezuela: [10.49, -66.88],
  cuba: [23.11, -82.37],
  brazil: [-15.79, -47.88],
  nigeria: [9.06, 7.49],
  sudan: [15.59, 32.53],
  ethiopia: [9.02, 38.75],
  congo: [-4.32, 15.31],
  myanmar: [16.87, 96.2],
  philippines: [14.6, 120.98],
  japan: [35.68, 139.69],
  "uk ": [51.51, -0.13],
  britain: [51.51, -0.13],
  france: [48.86, 2.35],
  germany: [52.52, 13.41],
  poland: [52.23, 21.01],
  arctic: [71.0, -8.0],
  "south china sea": [15.0, 114.0],
  crimea: [44.95, 34.1],
  houthi: [15.35, 44.21],
  hezbollah: [33.89, 35.5],
  lebanon: [33.89, 35.5],
  libya: [32.9, 13.18],
  somalia: [2.05, 45.34],
  mali: [12.64, -8.0],
  sahel: [15.0, 0.0],
  portugal: [38.72, -9.14],
  greenland: [64.17, -51.74],
  "xi jinping": [39.9, 116.4],
  putin: [55.75, 37.62],
  khamenei: [35.69, 51.39],
  "yoon suk": [37.57, 126.98],
  pahlavi: [35.69, 51.39],
  "strait of hormuz": [26.6, 56.3],
  "nobel peace": [59.91, 10.75],
  "us strike": [38.9, -77.04],
  "u.s.": [38.9, -77.04],
  // Additional locations for broader coverage
  canada: [45.42, -75.69],
  argentina: [-34.6, -58.38],
  colombia: [4.71, -74.07],
  chile: [-33.45, -70.67],
  peru: [-12.05, -77.04],
  ecuador: [-0.18, -78.47],
  egypt: [30.04, 31.24],
  morocco: [33.97, -6.85],
  tunisia: [36.81, 10.18],
  algeria: [36.75, 3.06],
  kenya: [-1.29, 36.82],
  "south africa": [-33.93, 18.42],
  tanzania: [-6.17, 35.74],
  mozambique: [-25.97, 32.59],
  angola: [-8.84, 13.23],
  zimbabwe: [-17.83, 31.05],
  australia: [-35.28, 149.13],
  "new zealand": [-41.29, 174.78],
  indonesia: [-6.21, 106.85],
  malaysia: [3.14, 101.69],
  vietnam: [21.03, 105.85],
  thailand: [13.76, 100.5],
  singapore: [1.35, 103.82],
  "hong kong": [22.32, 114.17],
  bangladesh: [23.81, 90.41],
  "sri lanka": [6.93, 79.85],
  nepal: [27.72, 85.32],
  cambodia: [11.56, 104.92],
  italy: [41.9, 12.5],
  spain: [40.42, -3.7],
  netherlands: [52.37, 4.9],
  belgium: [50.85, 4.35],
  sweden: [59.33, 18.07],
  norway: [59.91, 10.75],
  finland: [60.17, 24.94],
  denmark: [55.68, 12.57],
  switzerland: [46.95, 7.45],
  austria: [48.21, 16.37],
  greece: [37.98, 23.73],
  romania: [44.43, 26.1],
  czech: [50.08, 14.43],
  hungary: [47.5, 19.04],
  serbia: [44.79, 20.47],
  croatia: [45.81, 15.98],
  bulgaria: [42.7, 23.32],
  slovakia: [48.15, 17.11],
  "baltic": [56.95, 24.11],
  latvia: [56.95, 24.11],
  lithuania: [54.69, 25.28],
  estonia: [59.44, 24.75],
  georgia: [41.72, 44.79],
  armenia: [40.18, 44.51],
  azerbaijan: [40.41, 49.87],
  kazakhstan: [51.17, 71.45],
  uzbekistan: [41.3, 69.28],
  qatar: [25.29, 51.53],
  "united arab": [24.45, 54.65],
  uae: [24.45, 54.65],
  bahrain: [26.23, 50.59],
  kuwait: [29.38, 47.99],
  oman: [23.59, 58.55],
  jordan: [31.95, 35.93],
  "west bank": [31.9, 35.2],
  eritrea: [15.34, 38.93],
  djibouti: [11.59, 43.15],
  chad: [12.13, 15.06],
  "burkina faso": [12.37, -1.52],
  burkina: [12.37, -1.52],
  niger: [13.51, 2.11],
  senegal: [14.69, -17.44],
  "ivory coast": [5.35, -4.01],
  "côte d'ivoire": [5.35, -4.01],
  ghana: [5.56, -0.19],
  cameroon: [3.87, 11.52],
  "south sudan": [4.85, 31.58],
  hamas: [31.5, 34.47],
  zelensky: [50.45, 30.52],
  zelenskyy: [50.45, 30.52],
  kremlin: [55.75, 37.62],
  pentagon: [38.87, -77.06],
  "white house": [38.9, -77.04],
  congress: [38.9, -77.04],
  beijing: [39.9, 116.4],
  tehran: [35.69, 51.39],
  pyongyang: [39.02, 125.75],
  kabul: [34.53, 69.17],
  sanctions: [48.86, 2.35],
  nuclear: [39.02, 125.75],
  tariff: [38.9, -77.04],
  "trade war": [39.9, 116.4],
  ceasefire: [50.45, 30.52],
  "peace deal": [48.86, 2.35],
  missile: [35.69, 51.39],
  drone: [15.35, 44.21],
  invasion: [50.45, 30.52],
  coup: [9.06, 7.49],
  election: [38.9, -77.04],
  refugee: [48.86, 2.35],
  humanitarian: [48.86, 2.35],
};

function geolocateMarket(
  title: string,
  index: number
): [number, number] | null {
  const lower = title.toLowerCase();
  for (const [keyword, coords] of Object.entries(LOCATION_KEYWORDS)) {
    if (lower.includes(keyword)) {
      // Small offset using golden angle spiral to prevent overlap
      // Radius 0.4° (~45km) keeps markers clustered near the actual location
      const angle = index * 137.508 * (Math.PI / 180);
      const radius = 0.4 + (index % 5) * 0.15; // slight variation 0.4-1.0°
      return [
        coords[0] + Math.cos(angle) * radius,
        coords[1] + Math.sin(angle) * radius,
      ];
    }
  }
  return null;
}

// ─── Helpers ──────────────────────────────────────────────────────────
function formatVolume(vol: number): string {
  if (!vol || vol <= 0) return "$0";
  if (vol >= 1_000_000) return `$${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 1_000) return `$${(vol / 1_000).toFixed(0)}K`;
  return `$${vol.toFixed(0)}`;
}

function getYesPrice(market: GeoMarket): number {
  const prices = market.markets?.[0]?.outcomePrices;
  if (!prices || prices.length === 0) return 0;
  return Math.round(prices[0] * 100);
}

// ─── Ticker Bar (improved) ──────────────────────────────────────────
function TickerBar({ markets }: { markets: GeoMarket[] }) {
  if (markets.length === 0) return null;

  const items = markets.slice(0, 12).map((m) => ({
    title: m.title.length > 55 ? m.title.slice(0, 52) + "..." : m.title,
    price: getYesPrice(m),
    slug: m.slug,
    isLive: m.volume24h > 10000,
  }));

  const allItems = [...items, ...items];
  const duration = Math.max(15, items.length * 3);

  return (
    <div className="bg-black/80 backdrop-blur-md border-b border-white/[0.06] h-9 flex items-center overflow-hidden">
      <div className="flex-shrink-0 flex items-center gap-1.5 mx-3">
        <Radio className="w-3 h-3 text-red-500 animate-pulse" />
        <span className="text-[9px] font-bold text-red-500 uppercase tracking-widest">
          Breaking
        </span>
      </div>
      <div
        className="flex items-center gap-10 whitespace-nowrap geo-ticker-content"
        style={{ animationDuration: `${duration}s` }}
      >
        {allItems.map((item, i) => (
          <Link
            key={i}
            href={`/polymarket/event/${item.slug}`}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            {item.isLive ? (
              <span className="text-[8px] font-bold bg-red-500/20 text-red-400 px-1 py-px rounded uppercase tracking-wider">
                Live
              </span>
            ) : (
              <span className="w-1.5 h-1.5 rounded-[5px] bg-blue-400 flex-shrink-0" />
            )}
            <span className="text-[11px] text-white/60">{item.title}</span>
            <span
              className={`text-[11px] font-bold tabular-nums ${
                item.price >= 65
                  ? "text-emerald-400"
                  : item.price >= 35
                    ? "text-amber-400"
                    : "text-gray-400"
              }`}
            >
              {item.price > 50 ? "↑" : "↓"} {item.price}¢
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

// ─── Filter Panel ───────────────────────────────────────────────────
function FilterPanel({
  filters,
  onFiltersChange,
  isOpen,
  onToggle,
  uniqueTags,
  marketCount,
  eventCount,
  totalVolume,
}: {
  filters: MapFilters;
  onFiltersChange: (f: MapFilters) => void;
  isOpen: boolean;
  onToggle: () => void;
  uniqueTags: { slug: string; label: string }[];
  marketCount: number;
  eventCount: number;
  totalVolume: number;
}) {
  const toggleLayer = (layer: keyof MapFilters["layers"]) => {
    onFiltersChange({
      ...filters,
      layers: { ...filters.layers, [layer]: !filters.layers[layer] },
    });
  };

  const layerItems: {
    key: keyof MapFilters["layers"];
    label: string;
    icon: typeof Globe2;
    color: string;
  }[] = [
    { key: "markets", label: "Markets", icon: TrendingUp, color: "#60A5FA" },
    { key: "conflicts", label: "Conflicts", icon: Crosshair, color: "#EF4444" },
    { key: "diplomatic", label: "Diplomatic", icon: Shield, color: "#3B82F6" },
    { key: "zones", label: "Zones", icon: MapIcon, color: "#F97316" },
  ];

  return (
    <>
      {/* Toggle button — always visible */}
      <button
        onClick={onToggle}
        className="absolute top-12 left-3 z-[1001] flex items-center gap-1.5 px-2.5 py-1.5 rounded-[5px] text-[11px] font-medium bg-black/60 text-white/70 border border-white/[0.06] backdrop-blur-md hover:bg-black/80 hover:text-white transition-colors"
      >
        {isOpen ? (
          <PanelLeftClose className="w-3.5 h-3.5" />
        ) : (
          <PanelLeftOpen className="w-3.5 h-3.5" />
        )}
        <Layers className="w-3 h-3" />
      </button>

      {/* Panel */}
      <div
        className={`absolute top-[5.5rem] left-3 z-[1000] w-[260px] bg-black/70 backdrop-blur-xl border border-white/[0.06] rounded-[5px] transition-transform duration-200 ${
          isOpen
            ? "translate-x-0"
            : "-translate-x-[calc(100%+1rem)]"
        }`}
      >
        <div className="p-3.5 space-y-4">
          {/* ─── Layers ─────────────────── */}
          <div>
            <div className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2">
              Layers
            </div>
            <div className="grid grid-cols-2 gap-1.5">
              {layerItems.map(({ key, label, icon: Icon, color }) => (
                <button
                  key={key}
                  onClick={() => toggleLayer(key)}
                  className={`flex items-center gap-1.5 px-2 py-1.5 rounded-[5px] text-[11px] font-medium transition-colors ${
                    filters.layers[key]
                      ? "bg-white/15 text-white"
                      : "bg-black/40 text-white/40"
                  }`}
                >
                  <Icon
                    className="w-3 h-3"
                    style={{ color: filters.layers[key] ? color : undefined }}
                  />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* ─── Volume ─────────────────── */}
          <div>
            <div className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2">
              24h Volume
            </div>
            <div className="flex flex-wrap gap-1">
              {VOLUME_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() =>
                    onFiltersChange({ ...filters, volumeMin: opt.value })
                  }
                  className={`px-2 py-1 rounded-[5px] text-[10px] font-medium transition-colors ${
                    filters.volumeMin === opt.value
                      ? "bg-white/15 text-white"
                      : "bg-black/40 text-white/50 hover:bg-white/10"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* ─── Category ───────────────── */}
          {uniqueTags.length > 0 && (
            <div>
              <div className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2">
                Category
              </div>
              <div className="flex flex-wrap gap-1">
                <button
                  onClick={() =>
                    onFiltersChange({ ...filters, marketCategory: null })
                  }
                  className={`px-2 py-1 rounded-[5px] text-[10px] font-medium transition-colors ${
                    filters.marketCategory === null
                      ? "bg-white/15 text-white"
                      : "bg-black/40 text-white/50 hover:bg-white/10"
                  }`}
                >
                  All
                </button>
                {uniqueTags.slice(0, 8).map((tag) => (
                  <button
                    key={tag.slug}
                    onClick={() =>
                      onFiltersChange({
                        ...filters,
                        marketCategory: tag.slug,
                      })
                    }
                    className={`px-2 py-1 rounded-[5px] text-[10px] font-medium transition-colors capitalize ${
                      filters.marketCategory === tag.slug
                        ? "bg-white/15 text-white"
                        : "bg-black/40 text-white/50 hover:bg-white/10"
                    }`}
                  >
                    {tag.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ─── Status ─────────────────── */}
          <div>
            <div className="text-[10px] font-bold text-white/40 uppercase tracking-widest mb-2">
              Status
            </div>
            <div className="flex flex-wrap gap-1">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() =>
                    onFiltersChange({ ...filters, marketStatus: opt.value })
                  }
                  className={`px-2 py-1 rounded-[5px] text-[10px] font-medium transition-colors ${
                    filters.marketStatus === opt.value
                      ? "bg-white/15 text-white"
                      : "bg-black/40 text-white/50 hover:bg-white/10"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* ─── Stats summary ──────────── */}
          <div className="border-t border-white/[0.06] pt-3">
            <div className="flex items-center justify-between text-[10px] text-white/40">
              <span>
                {marketCount} markets &middot; {eventCount} events
              </span>
              {totalVolume > 0 && (
                <span className="text-emerald-400/70 font-medium">
                  {formatVolume(totalVolume)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT — Full-screen map experience
// ═══════════════════════════════════════════════════════════════════════
export default function GeopoliticsContent() {
  const [conflicts, setConflicts] = useState<EnhancedConflictEvent[]>([]);
  const [tensions, setTensions] = useState<EnhancedConflictEvent[]>([]);
  const [bilateral, setBilateral] = useState<BilateralTension[]>([]);
  const [geoMarkets, setGeoMarkets] = useState<GeoMarket[]>([]);
  const [totalMarketVolume, setTotalMarketVolume] = useState(0);
  const [filters, setFilters] = useState<MapFilters>(DEFAULT_FILTERS);
  const [panelOpen, setPanelOpen] = useState(false);
  const visRef = useVisibilityRef();

  // Open panel by default on desktop
  useEffect(() => {
    if (window.innerWidth >= 1024) setPanelOpen(true);
  }, []);

  // ─── Fast fetch: Markets (single API call, ~200ms) ──────────
  const fetchMarkets = useCallback(async () => {
    try {
      const res = await fetch(
        "/api/markets/events?tag_slug=geopolitics&order=volume24hr&ascending=false&limit=100"
      );
      const data = await res.json();
      const markets = data.markets || data || [];
      if (Array.isArray(markets)) {
        setGeoMarkets(markets);
        setTotalMarketVolume(
          markets.reduce(
            (sum: number, m: GeoMarket) => sum + (m.volume || 0),
            0
          )
        );
      }
    } catch {
      // Silent fail
    }
  }, []);

  // ─── Slow fetch: GDELT intelligence (reads from Redis cache) ───
  const fetchIntel = useCallback(async () => {
    try {
      const res = await fetch("/api/geopolitics/enhanced");
      const data = await res.json();
      if (data.success) {
        setConflicts(data.conflicts || []);
        setTensions(data.tensions || []);
        setBilateral(data.bilateral || []);
        // If backend is still populating cache, retry in 10s
        if (data.pending) {
          setTimeout(() => fetchIntel(), 10000);
        }
      }
    } catch {
      // Silent fail
    }
  }, []);

  // Fire both independently — map + markets appear first, GDELT layers arrive later
  useEffect(() => {
    fetchMarkets();
    fetchIntel();
    const marketsInterval = setInterval(() => { if (visRef.current) fetchMarkets(); }, 120_000);
    const intelInterval = setInterval(() => { if (visRef.current) fetchIntel(); }, 120_000);
    return () => {
      clearInterval(marketsInterval);
      clearInterval(intelInterval);
    };
  }, [fetchMarkets, fetchIntel]);

  // ─── Geolocate markets ────────────────────────────────────
  const geolocatedMarkets: GeolocatedMarket[] = useMemo(() => {
    return geoMarkets
      .map((market, i) => {
        const coords = geolocateMarket(market.title, i);
        if (!coords) return null;
        return { ...market, lat: coords[0], lon: coords[1] };
      })
      .filter(Boolean) as GeolocatedMarket[];
  }, [geoMarkets]);

  // ─── Unique tags from markets ──────────────────────────────
  const uniqueTags = useMemo(() => {
    const tagMap = new Map<string, string>();
    geoMarkets.forEach((m) =>
      m.tags?.forEach((t) => tagMap.set(t.slug, t.label))
    );
    return Array.from(tagMap.entries()).map(([slug, label]) => ({
      slug,
      label,
    }));
  }, [geoMarkets]);

  // ─── Apply filters ─────────────────────────────────────────
  const now = Date.now();
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

  const filteredMarkets = useMemo(() => {
    if (!filters.layers.markets) return [];
    return geolocatedMarkets.filter((m) => {
      if (filters.volumeMin > 0 && m.volume24h < filters.volumeMin)
        return false;
      if (
        filters.marketCategory &&
        !m.tags?.some((t) => t.slug === filters.marketCategory)
      )
        return false;
      if (filters.marketStatus === "new") {
        // No endDate heuristic — we treat recently active as "new" if volume24h is high relative to total
        // Alternatively, if we had a createdAt, we'd use it. For now, skip unless there's a better signal.
        // Use volume24h / volume ratio as proxy — high ratio = recently active/new
        if (m.volume > 0 && m.volume24h / m.volume < 0.1) return false;
      }
      if (filters.marketStatus === "closing-soon" && m.endDate) {
        const end = new Date(m.endDate).getTime();
        if (end - now > SEVEN_DAYS_MS || end < now) return false;
      } else if (filters.marketStatus === "closing-soon" && !m.endDate) {
        return false;
      }
      return true;
    });
  }, [
    geolocatedMarkets,
    filters.layers.markets,
    filters.volumeMin,
    filters.marketCategory,
    filters.marketStatus,
    now,
  ]);

  const filteredConflicts = filters.layers.conflicts ? conflicts : [];
  const filteredTensions = filters.layers.diplomatic ? tensions : [];
  const filteredBilateral = filters.layers.conflicts ? bilateral : [];

  const eventCount = filteredConflicts.length + filteredTensions.length;
  const marketCount = filteredMarkets.length;

  return (
    <div className="relative isolate w-full h-[calc(100dvh-3.5rem)] lg:h-[calc(100dvh-4rem)] overflow-hidden -mt-0.5">
      {/* ─── Full-screen map (renders immediately, data layers arrive progressively) */}
      <GeopoliticsFullMap
        marketMarkers={filteredMarkets}
        conflicts={filteredConflicts}
        tensions={filteredTensions}
        bilateral={filteredBilateral}
        showZones={filters.layers.zones}
      />

      {/* ─── Floating: Ticker ───────────────────────────────── */}
      <div className="absolute top-0 left-0 right-0 z-[1000]">
        <TickerBar markets={geoMarkets} />
      </div>

      {/* ─── Floating: Filter Panel ───────────────────────────── */}
      <FilterPanel
        filters={filters}
        onFiltersChange={setFilters}
        isOpen={panelOpen}
        onToggle={() => setPanelOpen((p) => !p)}
        uniqueTags={uniqueTags}
        marketCount={marketCount}
        eventCount={eventCount}
        totalVolume={totalMarketVolume}
      />

      {/* ─── Floating: Legend (bottom-left) ──────────────────── */}
      <div className="absolute bottom-16 sm:bottom-4 left-3 z-[1000]">
        <div className="flex items-center gap-3 text-[10px] text-white/40 bg-black/50 backdrop-blur-md rounded-[5px] px-3 py-2 border border-white/[0.06]">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-[5px] bg-red-500 geo-marker__pulse inline-block" />
            Conflict
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-[5px] bg-orange-500 inline-block" />
            High
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-[5px] bg-blue-500 inline-block" />
            Diplomatic
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="w-3 h-3 rounded-[5px] border-2 border-blue-400/50 inline-flex items-center justify-center"
              style={{ boxShadow: "0 0 6px rgba(96,165,250,0.3)" }}
            >
              <span className="w-1 h-1 rounded-[5px] bg-blue-400" />
            </span>
            Market
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-3 h-2 rounded-sm inline-block" style={{ background: "rgba(239,68,68,0.3)", border: "1px solid rgba(239,68,68,0.5)" }} />
            Zone
          </span>
        </div>
      </div>

      {/* ─── Floating: FrenFlow branding (bottom-right) ───── */}
      <div className="absolute bottom-16 sm:bottom-4 right-3 z-[1000]">
        <div className="text-[10px] text-white/20 bg-black/30 backdrop-blur-sm rounded-[5px] px-2.5 py-1.5 border border-white/[0.04]">
          Powered by GDELT &middot; Polymarket
        </div>
      </div>
    </div>
  );
}
